import { Button } from '@dfh-corp-sites/ui'
import BlockContent from '@sanity/block-content-to-react'
import React from 'react'

import { ProtocolResourceDocument } from '../../../graphql/api'
import { useApiQuery } from '../../../hooks/useApiQuery'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { buildResourceAssetUrl } from '../../../lib/library'
import Link from '../../global/Link'
import Spinner from '../../Spinner'
import { EScriptLink } from './EScriptLink'
import { Product } from './Product'
import ProtocolAddToCart from './ProtocolAddToCart'

export interface ProtocolModalProps {
  slug: string
  onOpenChange: (open: boolean) => void
}

const ProtocolModal: React.FC<ProtocolModalProps> = ({ slug, onOpenChange }) => {
  const { called, data, loading, error } = useApiQuery(ProtocolResourceDocument, {
    variables: { slug },
    skipWithoutToken: true,
  })
  useLogAndCaptureError(error)

  const protocol = data?.protocolResource
  const bodyRaw = protocol?.bodyRaw

  const hasAdjunctProducts = !!(protocol?.productsAdjunct && protocol.productsAdjunct.length > 0)

  if (!called || loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spinner loading />
      </div>
    )
  }

  return (
    <div
      data-cy="protocol-modal"
      className="h-[70vh] min-h-[500px] md:min-h-[650px] max-w-[912px] overflow-hidden py-8 relative"
    >
      <div className="h-[calc(100%-80px)] overflow-y-auto pb-8">
        <div className="px-5 md:px-10 mb-8 [&_h2]:text-[32px] [&_h2]:font-medium [&_h2]:mb-3.5">
          {bodyRaw && <BlockContent blocks={bodyRaw} />}
        </div>
        {protocol?.products && protocol.products.length > 0 && (
          <div className="border-t border-rule-grey">
            <div className="py-6 px-5 md:px-10">
              <EScriptLink products={protocol?.products} />
            </div>
            <div className="px-5 md:px-10 border-t border-rule-grey mb-8">
              <div className="text-lg font-medium pt-8 pb-3.5" id="protocol-modal-description">
                Products in this Protocol (Primary Support)
              </div>
              <div className="flex flex-col gap-5 lg:gap-2.5 justify-between items-center">
                {protocol?.products?.map((product) => {
                  if (!product?.product?.sku) return null
                  return <Product product={product} key={product.product.sku} />
                })}
              </div>
              {hasAdjunctProducts && (
                <div className="mt-6">
                  <div className="text-lg font-medium pb-3.5">
                    Products in this Protocol (Adjunct Support)
                  </div>
                  <div>
                    <div className="flex flex-col gap-6 lg:gap-2.5">
                      {protocol?.productsAdjunct?.map((productAdjunct) => {
                        if (!productAdjunct?.product?.sku) return null
                        return <Product product={productAdjunct} key={productAdjunct.product.sku} />
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 px-5 md:px-10 py-7 border-t border-rule-grey flex flex-col-reverse sm:flex-row flex-wrap justify-center sm:justify-between items-center gap-5">
        <button
          onClick={() => onOpenChange(false)}
          className="text-brand-orange underline text-sm font-medium"
        >
          Cancel
        </button>
        <div className="flex gap-5 flex-col-reverse sm:flex-row">
          <Button asChild variant="secondary" className="sm:py-2 sm:px-5 sm:text-lg">
            <Link to={buildResourceAssetUrl('protocol', slug)} target="_blank">
              View Protocol Document
            </Link>
          </Button>
          <ProtocolAddToCart
            products={protocol?.products}
            hasAdjunctProducts={hasAdjunctProducts}
          />
        </div>
      </div>
    </div>
  )
}

export default ProtocolModal
